import { createShortUrl } from '/js/shortUrl.js';

/**
 * Share helper:
 * Two-tap UX (iOS-safe):
 * - Tap #1: generates the short URL (Firestore) and updates button label to "Copia link".
 * - Tap #2: copies the already-generated short URL to clipboard (no `await` before copy).
 */

const ICON_SHARE = 'ph ph-share-network';
const ICON_COPY = 'ph ph-copy';
const ICON_CHECK = 'ph ph-check';

function setButtonVisual(shareButton, { label, iconClass } = {}) {
  const textNode = shareButton.querySelector('.button-text');
  const iconNode = shareButton.querySelector('i');

  if (iconClass) {
    if (iconNode) {
      iconNode.className = iconClass;
    } else {
      const newIcon = document.createElement('i');
      newIcon.className = iconClass;
      shareButton.prepend(newIcon);
      shareButton.insertAdjacentText('afterbegin', ' ');
    }
  }

  if (textNode) {
    if (typeof label === 'string') textNode.textContent = label;
    return;
  }

  if (iconNode) {
    if (typeof label === 'string') {
      shareButton.innerHTML = `${iconNode.outerHTML} ${label}`;
    }
    return;
  }

  if (typeof label === 'string') shareButton.textContent = label;
}

function fallbackCopyTextToClipboard(text) {
  try {
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.setAttribute('readonly', '');
    textarea.style.position = 'fixed';
    textarea.style.top = '-1000px';
    textarea.style.left = '-1000px';
    document.body.appendChild(textarea);

    textarea.focus();
    textarea.select();
    textarea.setSelectionRange(0, textarea.value.length);

    const ok = document.execCommand('copy');
    document.body.removeChild(textarea);
    return ok;
  } catch (error) {
    return false;
  }
}

async function copyToClipboard(text) {
  try {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch (error) {
    // fall through
  }

  const ok = fallbackCopyTextToClipboard(text);
  if (ok) return true;

  // Last resort: manual copy (does NOT auto-copy)
  window.prompt('Copia il link:', text);
  return false;
}

export async function handleShareClick(shareButton, options = {}) {
  if (!shareButton) return;

  const {
    campaign = 'sharing_link',
    generatingText = 'Generazione...',
    readyText = 'Copia link',
    copiedText = 'Link copiato',
    resetAfterMs = 1500
  } = options;

  if (!shareButton.dataset.shareOriginalContent) {
    shareButton.dataset.shareOriginalContent = shareButton.innerHTML;
  }

  const existingShortUrl = shareButton.dataset.shareShortUrl;
  const isReadyToCopy = shareButton.dataset.shareStage === 'ready' && !!existingShortUrl;

  // Tap #2: copy (iOS-safe because we don't `await` anything before choosing the text to copy)
  if (isReadyToCopy) {
    shareButton.disabled = true;
    try {
      await copyToClipboard(existingShortUrl);
      setButtonVisual(shareButton, { label: copiedText, iconClass: ICON_CHECK });
    } finally {
      setTimeout(() => {
        shareButton.disabled = false;
        setButtonVisual(shareButton, { label: readyText, iconClass: ICON_COPY });
      }, resetAfterMs);
    }
    return;
  }

  // Tap #1: generate short URL (Firestore)
  shareButton.disabled = true;
  setButtonVisual(shareButton, { label: generatingText, iconClass: ICON_SHARE });

  try {
    const shortUrl = await createShortUrl(window.location.href, campaign);
    shareButton.dataset.shareShortUrl = shortUrl;
    shareButton.dataset.shareStage = 'ready';
    setButtonVisual(shareButton, { label: readyText, iconClass: ICON_COPY });
  } catch (error) {
    console.error('Short URL creation failed', error);
    shareButton.dataset.shareStage = 'error';
    setButtonVisual(shareButton, { label: 'Errore, riprova', iconClass: ICON_SHARE });
    setTimeout(() => {
      shareButton.innerHTML = shareButton.dataset.shareOriginalContent;
      delete shareButton.dataset.shareStage;
      delete shareButton.dataset.shareShortUrl;
    }, 2000);
  }
  shareButton.disabled = false;
}


